import { Component } from 'react';
import cx from 'classnames';
import { arrayOf, bool, element, shape, string } from 'prop-types';
import { Button } from '@fff-web/fff-ui-shared';
import analytics, { events } from '../../../../utils/analytics';
import { boxExpired } from '../../../../utils/boxStatus';
import { campaignState } from '../../../../utils/campaignState';
import env from '../../../../utils/env';
import { scrollToComponent } from '../../../../utils/scroll-to-component';
import { SUBSCRIPTIONS_CATEGORY } from '../../../../utils/subscriptions';
import NoSubcriptions from '../../../Subscriptions/NoSubscriptions/NoSubscriptions';
import PageNav from '../PageNav/PageNav';
import ProductContainer from '../ProductContainer/ProductContainer';
import * as classNames from './Panel.module.scss';

const SUBSCRIPTION_TYPES = {
  GO_WITH_BOX: 'GO_WITH_BOX',
  NINETY_DAYS: 'NINETY_DAYS',
};

const getCurrentSaleNameCopy = (sale) => {
  switch (sale) {
    case 'EDIT':
      return 'edit sale';
    case 'ADDONS':
      return 'add-ons sale';
    default:
      return '';
  }
};

const getNoSubscriptionsCtaURL = (isEditCampaignOpen, isBoxSubscriptionType) => {
  if (isEditCampaignOpen) {
    return `/edit?category=${SUBSCRIPTIONS_CATEGORY[env.env]}`;
  }
  if (isBoxSubscriptionType) {
    return '/refills';
  }
  return '/shop';
};

class PanelView extends Component {
  static get propTypes() {
    return {
      sysdata: shape({
        userData: shape({}).isRequired,
        isMobile: bool.isRequired,
        isProspect: bool.isRequired,
      }).isRequired,
      displayOrderSummary: bool.isRequired,
      panelItems: arrayOf(shape({})),
      panelName: string.isRequired,
      isBoost: bool,
      status: string,
      hasBeenAssigned: bool,
      closedCTA: element,
      noItemCTA: element,
      reactiveCTA: element,
      orderSummary: element.isRequired,
      displayShopNowBtn: bool,
      addMore: element.isRequired,
      nextShipmentDate: string,
      sectionType: string,
      applyForFreeShipping: bool,
      displaySeparator: bool,
      currentSale: string,
      isEditCampaignOpen: bool,
    };
  }

  static get defaultProps() {
    return {
      reactiveCTA: null,
      isBoost: false,
      panelItems: [],
      status: null,
      nextShipmentDate: null,
      sectionType: null,
      hasBeenAssigned: null,
      closedCTA: null,
      noItemCTA: null,
      applyForFreeShipping: null,
      displayShopNowBtn: true,
      displaySeparator: true,
      currentSale: '',
      isEditCampaignOpen: false,
    };
  }

  state = {
    viewAll: false,
    currentPage: 1,
    pageLength: 5, // @TODO Get this number from the CMS
    initialDisplayAmount: 6, // @TODO Get this number from the CMS
  };

  hasPanelItems = (panelItems) => panelItems && panelItems.length > 0;

  showClosedMessage = (panelItems, openStatus) =>
    !this.hasPanelItems(panelItems) && openStatus === campaignState.CLOSED;

  showNoItemsCTA = (panelItems, openStatus) =>
    openStatus === campaignState.OPEN && !this.hasPanelItems(panelItems);

  getPageItems = (panelItems) => {
    const { currentPage, pageLength } = this.state;

    if (!panelItems || !panelItems.length) {
      return [];
    }

    const start = (currentPage - 1) * pageLength;
    const end = start + pageLength;

    return panelItems.slice(start, end);
  };

  render() {
    const {
      sysdata = {},
      displayOrderSummary,
      panelItems,
      panelName,
      isBoost,
      status,
      hasBeenAssigned,
      closedCTA,
      noItemCTA,
      reactiveCTA,
      orderSummary,
      addMore,
      nextShipmentDate,
      sectionType,
      applyForFreeShipping,
      displayShopNowBtn,
      displaySeparator,
      currentSale,
      isEditCampaignOpen,
    } = this.props;
    const { userData = {}, isMobile, isProspect } = sysdata;
    const isExpired = boxExpired(userData) && !sysdata.membershipInfo.isRefillSubscription;
    const { pageLength, initialDisplayAmount } = this.state;
    const pageItems = this.getPageItems(panelItems);
    const isBoxSubscriptionType = sectionType === SUBSCRIPTION_TYPES.GO_WITH_BOX;
    const isNinetyDaysSubscriptionType = sectionType === SUBSCRIPTION_TYPES.NINETY_DAYS;
    const isSubscriptionType = isBoxSubscriptionType || isNinetyDaysSubscriptionType;
    const isCampaignOpen = status === campaignState.OPEN;
    const displayAddMore =
      isNinetyDaysSubscriptionType || (!isNinetyDaysSubscriptionType && isCampaignOpen);
    const displayViewButton = !isMobile && !!panelItems && panelItems.length > initialDisplayAmount;
    const displaySidebar = displayOrderSummary || displayAddMore;
    const displayPanelItems = this.hasPanelItems(panelItems);
    const currentSaleNameCopy = getCurrentSaleNameCopy(currentSale);

    const viewButtonCTA = !this.state.viewAll ? 'View All' : 'View Less';

    return (
      <>
        <div className={classNames.panelContainer}>
          <div className={classNames.titleContainer}>
            <h1
              ref={(section) => {
                this.Header = section;
              }}
              className={classNames.title}
              data-testid={`${panelName}-campaign-title`}
            >
              {panelName}
            </h1>
            <div className={classNames.statusContainer}>
              <div className={classNames.status} data-testid={`${panelName}-campaign-status`}>
                {status && !isBoxSubscriptionType ? (
                  <>
                    Status: {status}
                    <div
                      className={cx(classNames.statusLight, {
                        [classNames.open]: isCampaignOpen,
                      })}
                    >
                      &nbsp;
                    </div>
                  </>
                ) : (
                  displayPanelItems && (
                    <h3
                      className={cx({
                        [classNames.noMargin]: currentSaleNameCopy,
                      })}
                      data-testid="next-renewal-date"
                    >{`Next renewal: ${nextShipmentDate}`}</h3>
                  )
                )}
              </div>
            </div>
            {currentSaleNameCopy && (
              <div className={classNames.reviewSubscriptions} data-testid="current-sale-copy">
                <p>
                  Check your cart to review subscriptions added to cart from the{' '}
                  {currentSaleNameCopy}
                </p>
              </div>
            )}
          </div>

          {!displayPanelItems && isSubscriptionType ? (
            <NoSubcriptions
              isBtnDisplayed={displayShopNowBtn}
              isActiveMember={!isExpired}
              ctaURL={getNoSubscriptionsCtaURL(isEditCampaignOpen, isBoxSubscriptionType)}
            />
          ) : (
            <>
              {/* Season is closed */}
              {this.showClosedMessage(panelItems, status) && !isExpired && !isProspect && (
                <div className={classNames.closedMsg}>{closedCTA}</div>
              )}

              {/* Season is open but I have no items */}
              {this.showNoItemsCTA(panelItems, status) && !isExpired && !isProspect && noItemCTA}

              {/* User's Subscription is Expired */}
              {isExpired && reactiveCTA}

              {/* User has items */}
              {displayPanelItems && (
                <div className={classNames.container}>
                  <div className={classNames.productGridWrapper}>
                    <ProductContainer
                      products={isMobile ? pageItems : panelItems}
                      open={isCampaignOpen}
                      viewAll={this.state.viewAll}
                      initialDisplayAmount={initialDisplayAmount}
                      isMobile={isMobile}
                      hasBeenAssigned={hasBeenAssigned}
                      isBoost={isBoost}
                    />
                  </div>
                  {isMobile && this.hasPanelItems(panelItems) && panelItems.length > pageLength && (
                    <PageNav
                      maxPageCount={4}
                      currentPage={this.state.currentPage}
                      totalPages={Math.ceil(panelItems.length / pageLength)}
                      onChange={(pg) => {
                        this.setState({ currentPage: pg });
                        scrollToComponent(this.Header, {
                          offset: 0,
                          align: 'top',
                          duration: 500,
                        });
                      }}
                    />
                  )}

                  {displaySidebar && (
                    <div className={classNames.summaryDetails}>
                      {displayOrderSummary && orderSummary}
                      {displayAddMore && !isProspect && addMore}
                      {applyForFreeShipping && (
                        <p className={classNames.freeShipping}>
                          Add more to this shipment to qualify for free shipping!
                        </p>
                      )}

                      {/* {Has addon items and is prospect member} */}
                      {displayAddMore && isProspect && (
                        <Button
                          styles={{ margin: '12px 0 0' }}
                          width="100%"
                          onClick={() => {
                            window.location = `${env.envURL}/get-the-box`;
                          }}
                        >
                          GET THE BOX
                        </Button>
                      )}
                      {displayViewButton && (
                        <Button
                          styles={{ margin: '12px 0 0' }}
                          width="100%"
                          onClick={() => {
                            this.setState({
                              viewAll: !this.state.viewAll,
                            });
                            analytics.track(events.CLICKED_ELEMENT, {
                              element_name: viewButtonCTA,
                              option_title: { panelName },
                            });
                          }}
                        >
                          {viewButtonCTA}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}
              {!displaySidebar && displayViewButton && (
                <Button
                  styles={{
                    margin: '16px auto',
                    display: 'block',
                  }}
                  width="200px"
                  onClick={() => {
                    this.setState({
                      viewAll: !this.state.viewAll,
                    });
                    analytics.track(events.CLICKED_ELEMENT, {
                      element_name: viewButtonCTA,
                      option_title: { panelName },
                    });
                  }}
                >
                  {viewButtonCTA}
                </Button>
              )}
              {isMobile &&
                this.hasPanelItems(panelItems) &&
                panelItems.length > pageLength &&
                !isExpired && (
                  <PageNav
                    maxPageCount={4}
                    currentPage={this.state.currentPage}
                    totalPages={Math.ceil(panelItems.length / pageLength)}
                    onChange={(pg) => {
                      this.setState({ currentPage: pg });
                      scrollToComponent(this.Header, {
                        offset: 0,
                        align: 'top',
                        duration: 500,
                      });
                    }}
                  />
                )}
            </>
          )}
        </div>
        {displaySeparator && <div className="hor-rule mar-h-40" />}
      </>
    );
  }
}

export default PanelView;
